import * as _ from 'lodash-es';
import type Block from 'quill/blots/block';
import type { BlockEmbed } from 'quill/blots/block';
import type Quill from 'quill/core';
import type { Parchment } from 'quill/core';
import QuillKeyboard from 'quill/modules/keyboard';
import { MOD_KEY } from '../constants/os';

export type Binding = NonNullable<QuillKeyboard['options']['bindings']>[string];
type GetBindingObject<B extends Binding> = B extends string | number
  ? never
  : B;

export type BindingObject = GetBindingObject<Binding>;

export const isTitleLine = (
  line: Block | BlockEmbed | Parchment.BlockBlot,
  quill: Quill,
) =>
  (!('noTitleLine' in quill) || !quill.noTitleLine) &&
  !!line &&
  line.offset(quill.scroll) === 0;

export default abstract class BaseKeyboard extends QuillKeyboard {
  constructor(quill: Quill, options: QuillKeyboard['options']) {
    super(quill, {
      ...options,
      bindings: patchListAutoFill(options.bindings),
    });
  }

  prependBinding(binding: BindingObject) {
    const keys = Array.isArray(binding.key) ? binding.key : [binding.key];
    keys.forEach((key) => {
      const singleBinding = { ...binding, key };
      if (singleBinding.shortKey) {
        singleBinding[MOD_KEY] = singleBinding.shortKey;
        delete singleBinding.shortKey;
      }

      this.bindings[singleBinding.key] = this.bindings[singleBinding.key] || [];
      this.bindings[singleBinding.key]?.unshift(singleBinding);
    });
  }

  removeBinding(binding: BindingObject) {
    const keys = Array.isArray(binding.key) ? binding.key : [binding.key];
    keys.forEach((key) => {
      if (!this.bindings[key]) return;

      const singleBinding = { ...binding, key };
      const bindingIndex =
        this.bindings[key]?.findIndex((item) =>
          _.isEqual(item, singleBinding),
        ) ?? -1;
      if (bindingIndex === -1) return;
      this.bindings[key]?.splice(bindingIndex, 1);
    });
  }
}

function patchListAutoFill(
  originalBindings?: QuillKeyboard['options']['bindings'],
) {
  const key = 'list autofill';
  const binding = originalBindings?.[key];
  if (typeof binding !== 'object') {
    return originalBindings;
  }

  const updatedBinding: BindingObject = {
    ...binding,
    format: {
      ...binding.format,
      hint: false,
    },
    handler(range, context) {
      return isTitleLine(context.line, this.quill)
        ? true
        : // @ts-expect-error
          binding.handler?.call(this, range, context);
    },
  };

  return { ...originalBindings, [key]: updatedBinding };
}
